/**
 * The brand statement band's motion: four drifting tiles, a headline that arrives word by word, and
 * one underline that draws itself.
 *
 * This replaced a particle text reveal that lived at the same element name. That effect sampled the
 * heading's glyphs into a canvas and dispersed them on hover, and it was the whole section. The slot
 * is now a composed statement with an eyebrow, a headline, a line of copy, a CTA and four floating
 * product tiles, so there is nothing left for a canvas to do. The element name is kept because it is
 * the section's published API.
 *
 * Four independent behaviours, and the only one that costs anything per frame is the first.
 *
 *   1. Tile drift, bound to scroll position. Linear, unsmoothed, one rAF per scroll burst.
 *   2. Tile entrance, once, when the group first appears.
 *   3. Headline word reveal, once, at 30 percent visibility.
 *   4. Underline draw, once, and early: it starts 300px before the band arrives so the stroke is
 *      already finished by the time the eyebrow is comfortably on screen.
 *
 * Two things about this theme that the obvious implementation gets wrong.
 *
 * Scroll position is not on `window`. Horizon scrolls `.page-wrapper` at desktop and the document at
 * mobile, so `window.scrollY` reads 0 for the whole page on a desktop and the drift never moves.
 * Everything here is derived from `getBoundingClientRect()`, which is true whatever element is doing
 * the scrolling, and the listener is registered in the capture phase because a `scroll` event fired
 * at an inner element does not bubble to `window`. It does still pass through the capture path, so
 * one capturing listener catches both scroll containers.
 *
 * The finished state is the default. Nothing here gates content on itself: the stylesheet ships the
 * words visible, the tiles at full opacity and the underline fully drawn, and this module sets
 * `data-armed` to opt the section into the animated starting state. With the module blocked, with
 * the element unupgraded, or under `prefers-reduced-motion: reduce`, the band is a finished
 * composition rather than four invisible tiles and a blank heading.
 *
 * Reduced motion is honoured live, not just at load. `rest()` removes `data-armed`, drops the
 * listeners and clears the inline transforms the drift wrote, because an inline transform would
 * outrank the stylesheet's resting position and leave the tiles parked wherever the last frame put
 * them.
 */

/*
 * The drift distance is a CSS custom property rather than a constant, so the breakpoint that halves
 * it on a phone lives beside the tile positions it has to stay clear of. Read on arm and on resize,
 * never per frame: `getComputedStyle` is a layout read.
 */
const TRAVEL_PROPERTY = '--nz-statement-travel';

class NastyTagline extends HTMLElement {
  connectedCallback() {
    this.tiles = Array.from(this.querySelectorAll('[data-statement-tile]'));
    this.tileGroup = this.querySelector('[data-statement-tiles]');
    this.wordGroup = this.querySelector('[data-statement-words]');
    this.rule = this.querySelector('[data-statement-underline]');

    this.calm = matchMedia('(prefers-reduced-motion: reduce)');
    this.watchers = [];
    this.frame = 0;
    this.travel = 0;

    this.tick = () => {
      this.frame = 0;
      this.drift();
    };
    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(this.tick);
    };
    this.onResize = () => {
      this.readTravel();
      this.drift();
    };
    this.onCalmChange = () => {
      if (this.calm.matches) this.rest();
      else this.arm();
    };

    this.calm.addEventListener('change', this.onCalmChange);

    if (this.calm.matches) this.rest();
    else this.arm();
  }

  disconnectedCallback() {
    this.rest();
    this.calm.removeEventListener('change', this.onCalmChange);
  }

  arm() {
    if (this.dataset.armed === 'true') return;
    this.dataset.armed = 'true';

    this.readTravel();
    this.drift();

    addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    addEventListener('resize', this.onResize, { passive: true });

    /*
     * Thresholds and margins are the ones the reference section shipped. The underline's 300px
     * vertical margin is the outlier and it is deliberate: a three second draw started when the
     * stroke is already centred finishes long after anybody has read the words above it.
     */
    this.watch(this.tileGroup, 0.1, '50px', 'tiles', 'in');
    this.watch(this.wordGroup, 0.3, '50px', 'words', 'in');
    this.watch(this.rule, 0.5, '300px 0px', 'underline', 'drawn');
  }

  rest() {
    delete this.dataset.armed;

    removeEventListener('scroll', this.onScroll, { capture: true });
    removeEventListener('resize', this.onResize);

    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;

    for (const watcher of this.watchers) watcher.disconnect();
    this.watchers.length = 0;

    for (const tile of this.tiles) tile.style.transform = '';
  }

  /* One shot: flip the flag the stylesheet keys off, then stop watching. */
  watch(target, threshold, rootMargin, flag, value) {
    if (!target || this.dataset[flag] === value) return;

    const watcher = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.dataset[flag] = value;
        watcher.disconnect();
      },
      { threshold, rootMargin }
    );

    watcher.observe(target);
    this.watchers.push(watcher);
  }

  readTravel() {
    this.travel = parseFloat(getComputedStyle(this).getPropertyValue(TRAVEL_PROPERTY)) || 0;
  }

  /*
   * Progress runs from the moment the band's top edge crosses the bottom of the viewport to the
   * moment its bottom edge leaves the top, so the full travel is spent over the band's own scroll
   * span plus one viewport. Written in viewport coordinates because there is no reliable document
   * scroll offset to read here.
   */
  drift() {
    if (!this.travel || !this.tiles.length) return;

    const view = innerHeight || 1;
    const box = this.getBoundingClientRect();
    const progress = Math.min(1, Math.max(0, (view - box.top) / (box.height + view)));

    for (const tile of this.tiles) {
      const shift = (tile.dataset.drift === 'up' ? 1 - progress : progress) * this.travel;
      /* translateX(-50%) is the tile's own centring, so the drift has to restate it. */
      tile.style.transform = `translateX(-50%) translateY(${shift.toFixed(2)}px)`;
    }
  }
}

if (!customElements.get('nasty-tagline')) {
  customElements.define('nasty-tagline', NastyTagline);
}
